import { useEffect, useRef, type CSSProperties } from "react";
import { tokens } from "../../theme";
import { bindVisualViewport } from "../../lib/visualViewport";
import type { AvatarWidgetController } from "../../hooks/useAvatarWidget";
import MessageList from "../MessageList";
import PanelLoadingShimmer from "../PanelLoadingShimmer";
import MobileComposer from "./MobileComposer";
import MobileHeader from "./MobileHeader";
import MobileHero from "./MobileHero";
import MobileQuestionList from "./MobileQuestionList";

interface MobileChatProps {
  widget: AvatarWidgetController;
}

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    background: tokens.surface.canvas,
    fontFamily: tokens.font.family,
    color: tokens.text.primary,
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    padding: tokens.space[4],
    background: tokens.surface.canvas,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[4],
    flexShrink: 0,
  },
} as const satisfies Record<string, CSSProperties>;

export default function MobileChat({ widget }: MobileChatProps) {
  const {
    isOpen,
    isPanelReady,
    messages,
    isStreaming,
    lastFailedText,
    widgetConfig,
    features,
    popularQuestions,
    showPopular,
    inputDisabled,
    sendMessage,
    retryLastMessage,
    attachments,
    attachmentNotice,
    addAttachments,
    removeAttachment,
    retryAttachment,
    dismissPanel,
  } = widget;

  const scrollRef = useRef<HTMLDivElement>(null);
  const displayName = widgetConfig?.name?.trim() || "Avatar AI";
  const inConversation = messages.length > 0;

  useEffect(() => bindVisualViewport(), []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  if (!isOpen) return null;
  if (!isPanelReady) return <PanelLoadingShimmer />;

  return (
    <div style={styles.root}>
      <MobileHeader name={displayName} onBack={dismissPanel} />
      <div ref={scrollRef} style={styles.body}>
        <div style={styles.stack}>
          {showPopular && !inConversation ? (
            <>
              <MobileHero name={displayName} voiceEnabled={features.voice} />
              <MobileQuestionList
                questions={popularQuestions}
                disabled={inputDisabled}
                onSelectQuestion={sendMessage}
              />
            </>
          ) : null}
          <MessageList
            messages={messages}
            lastFailedText={lastFailedText}
            onRetry={retryLastMessage}
          />
        </div>
      </div>
      <MobileComposer
        name={displayName}
        onSend={sendMessage}
        disabled={inputDisabled}
        features={features}
        attachments={attachments}
        onAttach={addAttachments}
        onRemoveAttachment={removeAttachment}
        onRetryAttachment={retryAttachment}
        attachmentNotice={attachmentNotice}
      />
    </div>
  );
}
